import { useState, useEffect, useMemo } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { InputText } from 'primereact/inputtext';
import { Card } from 'primereact/card';
import { Badge } from 'primereact/badge';
import { ProgressBar } from 'primereact/progressbar';
import { Dropdown } from 'primereact/dropdown';

const TEMPLATE_OPTIONS = [
  { code: 'template_1', name: 'Template 1 (Editorial)' },
  { code: 'template_2', name: 'Template 2 (Places)' },
];

export default function BlogList() {
  const [blogs, setBlogs] = useState([]);
  const [loadingPages, setLoadingPages] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [templateFilter, setTemplateFilter] = useState(null);
  const navigate = useNavigate();
  const public_url = process.env.REACT_APP_BORDERGARITA_PUBLIC_APP_URL || '';

  useEffect(() => {
    api
      .get('/blogs_pages/summary')
      .then((response) => {
        setBlogs(response.data || []);
        setLoadingPages(false);
      })
      .catch((error) => {
        console.error('Error fetching blogs:', error);
        setLoadingPages(false);
      });
  }, []);

  const blogsFiltered = useMemo(() => {
    let filtered = [...blogs];
    if (templateFilter) {
      filtered = filtered.filter((b) => (b.template || 'template_1') === templateFilter);
    }
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (b) =>
          (b.full_slug && b.full_slug.toLowerCase().includes(q)) ||
          (b.title && b.title.toLowerCase().includes(q)) ||
          (b.hero?.title && b.hero.title.toLowerCase().includes(q)) ||
          (b.slug && b.slug.toLowerCase().includes(q))
      );
    }
    return filtered;
  }, [blogs, searchTerm, templateFilter]);

  return (
    <Card>
      <div className="card p-4">
        <div className="grid mb-4">
          <div className="col-6 flex align-items-center">
            <h2 className="m-0 main-text font-bold vertical-align-middle">
              <span>Blogs</span>
              <Badge
                className="ml-2 b-4 relative"
                value={blogsFiltered.length ?? 0}
                style={{ bottom: '3px', backgroundColor: '#2020A0' }}
              />
            </h2>
          </div>
          <div className="col-6 flex justify-content-end">
            <Button
              label="Add blog"
              icon="pi pi-plus"
              className="p-button-primary p-button-sm secondary-button"
              style={{ minWidth: 120 }}
              onClick={() => navigate('/blogs/new')}
            />
          </div>
        </div>

        <div className="grid mb-4">
          <div className="col-12 flex gap-2 align-items-end">
            <span style={{ minWidth: 220 }}>
              <label style={{ fontSize: '0.95rem' }}>
                <strong>Template</strong>
              </label>
              <Dropdown
                value={templateFilter}
                options={TEMPLATE_OPTIONS}
                showClear
                onChange={(e) => setTemplateFilter(e.value)}
                optionLabel="name"
                optionValue="code"
                placeholder="All templates"
                className="w-full"
              />
            </span>
            <div className="flex flex-column" style={{ minWidth: 250 }}>
              <label style={{ fontSize: '0.95rem' }}>
                <strong>Search</strong>
              </label>
              <InputText
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full p-button-sm"
                placeholder="Search by URL or title"
                style={{ fontSize: '0.95rem' }}
              />
            </div>
            <Button
              label="Clear search"
              icon="pi pi-times"
              className="p-button-text text-danger p-button-sm"
              onClick={() => {
                setSearchTerm('');
                setTemplateFilter(null);
              }}
            />
          </div>
        </div>

        {loadingPages && (
          <div
            className="flex flex-column align-items-center justify-content-center"
            style={{ minHeight: '300px' }}
          >
            <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem', color: '#2020A0' }} />
            <div style={{ marginTop: '1rem', fontSize: '1.2rem', color: '#555' }}>
              Loading blogs...
            </div>
          </div>
        )}

        {!loadingPages && (
          <DataTable
            value={blogsFiltered}
            emptyMessage="No blogs found."
            className="p-datatable-sm"
            showGridlines
            stripedRows
          >
            <Column
              field="full_slug"
              header="URL"
              body={(rowData) => (
                <strong>
                  <a
                    href={`${public_url}${rowData.full_slug}`}
                    target="_blank"
                    title={`${public_url}${rowData.full_slug}`}
                    rel="noopener noreferrer"
                  >
                    {rowData.full_slug}
                  </a>
                </strong>
              )}
            />
            <Column field="lang" header="Language" headerStyle={{ textAlign: 'center' }} />
            <Column
              header="Template"
              body={(rowData) => {
                const t = rowData.template || 'template_1';
                return t === 'template_2' ? 'Places (T2)' : 'Editorial (T1)';
              }}
            />
            <Column
              header="Title"
              body={(rowData) => rowData.hero?.title || rowData.title || '—'}
            />
            <Column
              header="Progress"
              body={(rowData) => {
                const percentage = rowData.completionPercentage || 0;
                return (
                  <div className="flex align-items-center gap-2">
                    <ProgressBar
                      value={percentage}
                      showValue={false}
                      color={
                        percentage === 100
                          ? '#22c55e'
                          : percentage >= 70
                            ? '#3b82f6'
                            : '#f59e0b'
                      }
                      style={{ width: '50px', height: '10px' }}
                    />
                    <span>{percentage}%</span>
                  </div>
                );
              }}
            />
            <Column
              header="Configure"
              body={(rowData) => (
                <Button
                  icon="pi pi-cog"
                  className="p-button-text p-button-sm"
                  onClick={() => navigate(`/blogs/edit/${rowData._id}`)}
                  tooltip="Edit blog"
                  tooltipOptions={{ position: 'top' }}
                />
              )}
              style={{ width: '6rem', textAlign: 'center' }}
            />
          </DataTable>
        )}
      </div>
    </Card>
  );
}
